/**
 * Playing with the keyboard.
 *
 * The player moves a selection between the blocks they could launch: the
 * front block of each column (the bottom row on screen) and the tray slots
 * (the row above). Keys:
 *
 * - Left / Right: move along the current row.
 * - Up / Down: move between the tray row and the columns row.
 * - Enter or Space: launch the selected block (or, after a win or loss, carry on).
 * - 1 to 9: launch that column's front block directly.
 * - S: hear a summary of the board (see narration.ts).
 *
 * This file only decides what a key press means. main.ts carries it out, so
 * this logic stays easy to test.
 */
import type { GameState, Source } from "./state";

/** What a key press asks for. */
export type KeyAction =
	/** Select a different block (and describe it). */
	| { kind: "select"; selection: Source }
	/** Launch the block at `source` (which also becomes the selection). */
	| { kind: "launch"; source: Source }
	/** The level is over: go on (next level after a win, retry after a loss). */
	| { kind: "continue" }
	/** Describe the whole board. */
	| { kind: "summary" };

/** Where the selection starts: the first column. */
export const START: Source = { from: "column", index: 0 };

/**
 * What pressing `key` (a `KeyboardEvent.key` value, such as "ArrowLeft")
 * should do, given the current selection. Returns null for keys the game
 * doesn't use, so the browser can handle them as usual (Tab, for example).
 */
export function keyAction(
	key: string,
	state: GameState,
	selection: Source | null,
): KeyAction | null {
	const current = selection ?? START;
	if (key === "s" || key === "S") return { kind: "summary" };

	if (state.status !== "playing") {
		return key === "Enter" || key === " " ? { kind: "continue" } : null;
	}

	switch (key) {
		case "ArrowLeft":
			return {
				kind: "select",
				selection: {
					...current,
					index: clamp(current.index - 1, rowLength(state, current)),
				},
			};
		case "ArrowRight":
			return {
				kind: "select",
				selection: {
					...current,
					index: clamp(current.index + 1, rowLength(state, current)),
				},
			};
		case "ArrowUp":
		case "ArrowDown": {
			// The tray sits above the columns: Up goes to the tray, Down to the columns.
			const from = key === "ArrowUp" ? "tray" : "column";
			if (state.tray.length === 0 && from === "tray")
				return { kind: "select", selection: current };
			const row: Source = { from, index: 0 };
			return {
				kind: "select",
				selection: { from, index: clamp(current.index, rowLength(state, row)) },
			};
		}
		case "Enter":
		case " ":
			return { kind: "launch", source: current };
	}

	// Digits launch a column directly: "1" is the first column.
	if (/^[1-9]$/.test(key)) {
		const index = Number(key) - 1;
		if (index < state.columns.length)
			return { kind: "launch", source: { from: "column", index } };
	}
	return null;
}

/** How many places the selection's row has: columns, or tray slots. */
function rowLength(state: GameState, source: Source): number {
	return source.from === "column" ? state.columns.length : state.tray.length;
}

/** Keep `index` within 0 to `length - 1`. */
function clamp(index: number, length: number): number {
	return Math.max(0, Math.min(length - 1, index));
}
